import {ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToolService } from '../../services/tool';
import { Tool } from '../../models/tool';

@Component({
  standalone: true,
  selector: 'app-admin',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './admin.html',
  styleUrl: './admin.scss',
})
export class Admin implements OnInit {
  tools: Tool[] = [];
  toolForm: FormGroup;
  showForm: boolean = false;
  editingId: number | null = null;
  successMessage: string = '';
  errorMessage: string = '';

  categories = ['Data Quality', 'Validation', 'Log Processing', 'Normalization', 'Analysis', 'Optimization'];

  // Icons available under /public/assets/icons. The `value` is stored on the
  // tool and used directly as the card image src; `label` is shown in the form.
  icons = [
    { label: 'gr.svg', value: 'assets/icons/gr.svg' },
    { label: 'header.svg', value: 'assets/icons/header.svg' },
    { label: 'logmerge.svg', value: 'assets/icons/logmerge.svg' },
    { label: 'wtv.svg', value: 'assets/icons/wtv.svg' },
    { label: 'whc.png', value: 'assets/icons/whc.png' },
  ];

  constructor(private toolService: ToolService, private fb: FormBuilder, private cdr: ChangeDetectorRef) {
    this.toolForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      category: ['', Validators.required],
      icon: [''],
      url: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+/)]],
    });
  }

  ngOnInit(): void {
    this.toolService.tools$.subscribe((data: Tool[]) => {
      this.tools = data;
      this.cdr.markForCheck();
    });

    this.loadTools();
  }

  loadTools(): void {
    this.toolService.getTools(true).subscribe({
      next: (data: Tool[]) => {
        this.errorMessage = '';
      },
      error: () => {
        this.tools = [];
        this.errorMessage = 'Unable to load tools from the backend.';
      },
    });
  }

  private resetSuccessMessage(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  toggleForm(): void {
    this.showForm = !this.showForm;
    this.editingId = null;
    this.toolForm.reset();
    this.successMessage = '';
    this.errorMessage = '';
  }

  addTool(): void {
    if (this.toolForm.invalid) {
      this.errorMessage = 'Please fill in all fields correctly';
      return;
    }

    const newTool = {
      ...this.toolForm.value,
      icon: this.toolForm.value.icon || null,
    };

    this.toolService.createTool(newTool).subscribe({
      next: () => {
        this.successMessage = 'Tool added successfully!';
        this.errorMessage = '';
        this.toolForm.reset();
        this.showForm = false;
        this.resetSuccessMessage();
      },
      error: () => {
        this.errorMessage = 'Failed to add tool. Please try again.';
      },
    });
  }

  editTool(tool: Tool): void {
    this.editingId = tool.id;
    this.showForm = true;
    this.toolForm.patchValue({
      name: tool.name,
      description: tool.description,
      category: tool.category,
      icon: tool.icon,
      url: tool.url,
    });
  }

  updateTool(): void {
    if (this.toolForm.invalid || this.editingId === null) {
      this.errorMessage = 'Please fill in all fields correctly';
      return;
    }

    const toolId = this.editingId;
    const updatedTool = {
      ...this.toolForm.value,
      icon: this.toolForm.value.icon || null,
    };

    this.toolService.updateTool(toolId, updatedTool).subscribe({
      next: () => {
        this.successMessage = 'Tool updated successfully!';
        this.errorMessage = '';
        this.toolForm.reset();
        this.showForm = false;
        this.editingId = null;
        this.resetSuccessMessage();
      },
      error: () => {
        this.errorMessage = 'Failed to update tool. Please try again.';
      },
    });
  }

  deleteTool(id: number): void {
    if (confirm('Are you sure you want to delete this tool?')) {
      this.toolService.deleteTool(id).subscribe({
        next: () => {
          this.successMessage = 'Tool deleted successfully!';
          this.errorMessage = '';
          this.resetSuccessMessage();
        },
        error: () => {
          this.errorMessage = 'Failed to delete tool. Please try again.';
        },
      });
    }
  }

  cancelEdit(): void {
    this.showForm = false;
    this.editingId = null;
    this.toolForm.reset();
    this.errorMessage = '';
  }
}
